import React, { useCallback, useEffect, useState } from 'react';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Button from '../../shared/components/ui/Button';
import Search from '../../shared/components/ui/Search';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import filter from '../../../modules/shared/assets/svg/filter.svg';
import Table from '../../shared/components/layout/Table';
import { transformData } from '../../super-admin/utils/transformData';
import Modal from '../../shared/components/ui/Modal';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import {
  createPartner,
  getMyPartners,
  resendEmailInvites
} from '../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';
import PaginationComponent from '../../shared/components/PaginationComponent';
import AddPartner from '../components/forms/AddPartner';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { Plus } from 'lucide-react';

const PartnersList = () => {
  const userId = Cookies.get('userId');
  /* 
    Hooks
  */
  const navigate = useNavigate();

  // State for pagination, search, modal control, and vendors data
  const [limit] = useState(500); // items per page
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [OpenAddVendor, setOpenAddVendor] = useState(false);
  const [vendors, setVendors] = useState([]);
  const { collapsed } = useSidebar();
  const [filteredData, setFilteredData] = useState([]);
  const [activeTab, setActiveTab] = useState('myPartners'); // 'myPartners' or 'marketplace'
  const [modalType, setModalType] = useState('Add');
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch vendors data with pagination
  const {
    data: allVendors,
    refetch: refetchPartners,
    isFetching
  } = useQuery({
    queryKey: ['vendor', activeTab],
    queryFn: () => activeTab === 'myPartners' && getMyPartners(1, limit),
    keepPreviousData: true
  });

  //refetch vendors when component mounts

  // Track page view
  useEffect(() => {
    mixpanel.track('Partners List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchPartners();
  }, []);

  console.log('vendors', allVendors);

  //stare the all vendors in the vendors state
  useEffect(() => {
    if (allVendors?.data) {
      setVendors(allVendors?.data);
      setFilteredData(allVendors?.data);
    }
  }, [allVendors?.data]);

  const { mutate: handleCreatePartner } = useMutation({
    mutationFn: (payload) => createPartner(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (data) => {
      setIsSubmitting(false);
      toast.success('Partner created successfully');
      refetchPartners();
      setVendors([...vendors, data]);
      setFilteredData([...vendors, data]);
      setOpenAddVendor(false);
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating new partner', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating new partner');
    }
  });

  const handleSearch = (data) => {
    setSearchQuery(data);
  };

  const handleOnClickSearch = () => {
    const filtered = vendors.filter((item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredData(filtered);
    setSearchQuery('');
  };

  /* 
    Event handlers
  */
  const handleToggleAddPartner = () => {
    setModalType('Add');
    setOpenAddVendor(!OpenAddVendor);
  };

  const vendorsDataFormat = [
    'id',
    'businessName',
    'name',
    'email',
    'phone_number',
    'status',
    'invite',
    'actions'
  ];

  const { reset } = useForm();

  useEffect(() => {
    setFilteredData(vendors);
  }, [vendors]);

  const handleSearchChange = useCallback(
    (incomingQuery) => {
      const lowerCaseQuery = incomingQuery.toLowerCase();

      if (incomingQuery) {
        const filteredVendors = vendors.filter((vendor) => {
          // Search across multiple fields
          return (
            vendor.businessName?.toLowerCase().includes(lowerCaseQuery) ||
            vendor.user?.name?.toLowerCase().includes(lowerCaseQuery) ||
            vendor.user?.email?.toLowerCase().includes(lowerCaseQuery) ||
            vendor.user?.phone_number?.toLowerCase().includes(lowerCaseQuery)
          );
        });

        setFilteredData(filteredVendors);
      } else {
        // Reset to all vendors if query is empty
        setFilteredData(vendors);
      }
    },
    [vendors]
  );

  const handleVendorFormat = (item) => {
    return {
      id: item.id,
      name: item?.user?.name,
      businessName: item?.businessName,
      email: item?.user?.email,
      phone_number: item?.user?.phone_number,
      status: item?.is_onboarded == true ? 'Active' : 'Inactive',
      invite: `Invite ${item?.user?.is_active}`
    };
  };

  // onSubmit button function for adding vendor
  const onSubmit_Vendor = (data) => {
    const newVendor = { ...data, id: vendors.length + 1 };
    setVendors([...vendors, newVendor]);
    setFilteredData([...vendors, newVendor]);
    setOpenAddVendor(false);
    toast.success('Vendor created successfully');
    reset();
  };

  // Add tab change handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setVendors([]); // Clear current vendors
    setFilteredData([]); // Clear filtered data
    setCurrentPage(1); // Reset to first page
  };

  // Transformation of the vendor data
  const transformedData = filteredData?.map((item) =>
    transformData(handleVendorFormat(item), vendorsDataFormat)
  );

  //wriet useMutation for resendEmailInvites
  const { mutate: resendInvite } = useMutation({
    mutationFn: (payload) => resendEmailInvites(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async (res) => {
      setIsSubmitting(false);
      console.log({ res });
      toast.success('Invite sent successfully');
      console.log('Invite sent successfully');
      await refetchPartners(); // UI data update
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error sending invite');
    }
  });

  const handleResendInvite = (id) => {
    const users = filteredData?.filter((user) => parseInt(user.id) === parseInt(id));
    const email = users[0]?.user?.email;
    resendInvite({ email });
  };

  return (
    <>
      {/* Add vendor form */}
      {OpenAddVendor && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={56}
          name="Add Partner"
          onClose={handleToggleAddPartner}>
          <AddPartner
            isSubmitting={isSubmitting}
            onSubmit={handleCreatePartner}
            onClose={handleToggleAddPartner}
            modalType={modalType}
            setModalType={setModalType}
            setIsSubmitting={setIsSubmitting}
          />
        </Modal>
      )}

      <Layout>
        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header data={{ name: 'My Partners' }} />
        </div>

        {/* Search and Add button bar */}
        <div
          className={`flex z-20 items-center justify-between px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100 shadow-sm transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Search onChange={handleSearchChange} query={searchQuery} onClick={handleOnClickSearch} />
        </div>

        <div className="fixed bottom-8 right-8 z-50">
          <Button
            displayType="primary"
            onClick={() => {
              handleToggleAddPartner();
            }}
            customStyle={{
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
            <Plus size={24} color="white" />
          </Button>
        </div>

        {/* Grid - adjusted margin top to account for new tab position */}
        <GridWrapper width={100}>
          <div className="mt-[10rem] relative table-container h-[calc(100vh-11.5rem)] overflow-auto">
            <style>{`
              .table-container .table-body {
                position: static !important;
                overflow-y: visible !important;
                max-height: none !important;
                height: auto !important;
                top: auto !important;
                padding-bottom: 0 !important;
              }
              .table-container .table-container {
                height: auto !important;
              }
              .table-container > div {
                height: auto !important;
              }

            `}</style>
            <Table
              headers={[
                'Business Name',
                'Contact Person Name',
                'Email',
                'Phone',
                'Status',
                'Invite'
              ]}
              data={transformedData}
              identifier={'id'}
              isEditEnable={false}
              onResendInvite={(id) => handleResendInvite(id)}
              isWrap={true}
            />
          </div>
        </GridWrapper>
      </Layout>
    </>
  );
};

export default PartnersList;
